import { apiClient } from '@/api/core/apiClient';
import { API_CONFIG } from '@/api/core/apiConfig';
import type { ApiResponse } from '@/api/shared/types';
import { buildQueryString } from '@/api/shared/queryBuilder';

import type {
  LC_ExportDeviceListIn,
  LC_GetDeviceListIn,
  LC_GetDeviceListOut,
  LC_GetDeviceSummaryOut,
} from './device.types';


export class DeviceService {
  /**
   * Get device summary counters (total, connected, disconnected)
   */
  async getSummary(signal?: AbortSignal): Promise<ApiResponse<LC_GetDeviceSummaryOut>> {
    return apiClient.get<LC_GetDeviceSummaryOut>(
      API_CONFIG.ENDPOINTS.DEVICES.SUMMARY,
      { signal },
    );
  }

  /**
   * Get paginated, searchable and sortable device list
   */
  async list(
    params: LC_GetDeviceListIn,
    signal?: AbortSignal,
  ): Promise<ApiResponse<LC_GetDeviceListOut>> {
    const query = buildQueryString({
      page: params.page,
      pageSize: params.pageSize,
      searchText: params.searchText,
      orderBy: params.orderBy,
      orderDirection: params.orderDirection,
      typeFilter: params.typeFilter,
      statusFilter: params.statusFilter,
    });
    return apiClient.get<LC_GetDeviceListOut>(
      `${API_CONFIG.ENDPOINTS.DEVICES.LIST}${query}`,
      { signal },
    );
  }

  /**
   * Export the device list to an Excel file.
   * Downloads the file directly as a Blob; filename comes from the server.
   */
  async export(
    params: LC_ExportDeviceListIn,
  ): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    const query = buildQueryString({
      searchText: params.searchText,
      typeFilter: params.typeFilter,
      statusFilter: params.statusFilter,
    });
    const response = await apiClient.downloadBlobWithFilename(
      `${API_CONFIG.ENDPOINTS.DEVICES.EXPORT}${query}`,
      { method: 'GET' },
    );

    if (response.success && response.data) {
      return response;
    }

    return { ...response, data: null };
  }
}

export const deviceService = new DeviceService();
